'use client'

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { noop } from 'lodash'

import { UiState } from '@marketplace-web/shared/ui-state-util'
import { isResponseError } from '@marketplace-web/core-api/api-client-util'

import useFieldErrors from '../hooks/useFieldErrors'
import { DynamicAttribute } from '../types'
import {
  DynamicAttributeModel,
  AdditionalAttributeModel,
  NormalizedAttributes,
} from '../types/dynamic-attribute'
import { DynamicAttributesContextType } from '../types/context'
import { InitialDynamicAttributesData } from '../types/initial-data'
import { ItemAttributeModel } from '../types/item-edit'
import { getCategoryAttributes } from '../api'
import { reduceAttributes } from '../utils/dynamic-attribute'
import { transformDynamicAttributesToModel } from '../transformers/dynamic-attribute'
import { useAttributesContext } from './AttributesContext'

export const DynamicAttributesContext = createContext<DynamicAttributesContextType>({
  uiState: UiState.Idle,
  selectedDynamicAttributes: [],
  fetchCategoryAttributes: () => Promise.resolve(),
  getDynamicAttributeByCode: () => undefined,
  getAdditionalAttributesByCode: () => undefined,
  setSelectedDynamicAttributeValues: noop,
  getSelectedDynamicAttributeValue: () => [],
  setSelectedDynamicAttributeValue: noop,
})

export const DynamicAttributesContextProvider = ({
  initialDynamicAttributesData,
  children,
}: {
  initialDynamicAttributesData: InitialDynamicAttributesData
  children: ReactNode
}) => {
  const { setCatalogId } = useAttributesContext()

  const initialDynamicAttributesState = reduceAttributes(
    initialDynamicAttributesData?.dynamicAttributes,
  )
  const initialAdditionalAttributesState = reduceAttributes(
    initialDynamicAttributesData?.additionalAttributes,
  )
  const initialCatalogAttributesState = reduceAttributes(
    initialDynamicAttributesData?.itemAttributes,
    'ids',
  )

  const [dynamicAttributes, setDynamicAttributes] = useState<
    NormalizedAttributes<DynamicAttributeModel>
  >(initialDynamicAttributesState)
  const [additionalAttributes, setAdditionalAttributes] = useState<
    NormalizedAttributes<AdditionalAttributeModel>
  >(initialAdditionalAttributesState)
  const [catalogAttributes, setCatalogAttributes] = useState<
    NormalizedAttributes<ItemAttributeModel, 'ids'>
  >(initialCatalogAttributesState)
  const [uiState, setUiState] = useState<UiState>(
    initialDynamicAttributesData ? UiState.Success : UiState.Idle,
  )

  const { removeFieldError } = useFieldErrors()

  const fetchCategoryAttributes = useCallback(
    async (catalogId: number | null) => {
      if (!catalogId) return

      setUiState(UiState.Pending)
      const response = await getCategoryAttributes(catalogId)

      if (isResponseError(response) || !response.attributes) {
        setCatalogId(null)
        setUiState(UiState.Failure)

        return
      }

      setDynamicAttributes(reduceAttributes(transformDynamicAttributesToModel(response.attributes)))
      setAdditionalAttributes(reduceAttributes(response.additional_attributes))

      setUiState(UiState.Success)
    },
    [setCatalogId],
  )

  const setSelectedDynamicAttributeValues = useCallback(
    (selectedValues: Array<ItemAttributeModel> | undefined) => {
      if (!selectedValues) {
        setCatalogAttributes({ byName: {}, names: [] })

        return
      }

      setCatalogAttributes(reduceAttributes(selectedValues, 'ids'))
    },
    [],
  )

  const selectedDynamicAttributes = useMemo(
    () =>
      catalogAttributes.names
        .map(name => {
          if (!dynamicAttributes.byName[name]) return null

          return {
            field: name,
            value: catalogAttributes.byName[name],
          }
        })
        .filter((attribute): attribute is DynamicAttribute => !!attribute),
    [dynamicAttributes.byName, catalogAttributes.names, catalogAttributes.byName],
  )

  const getDynamicAttributeByCode = useCallback(
    (fieldName: string) => dynamicAttributes.byName[fieldName] || undefined,
    [dynamicAttributes.byName],
  )

  const getAdditionalAttributesByCode = useCallback(
    (fieldName: string) => additionalAttributes.byName[fieldName] || undefined,
    [additionalAttributes.byName],
  )

  const getSelectedDynamicAttributeValue = useCallback(
    (fieldName: string) => catalogAttributes.byName[fieldName] || [],
    [catalogAttributes.byName],
  )

  const setSelectedDynamicAttributeValue = useCallback(
    (field: string, value: Array<number>) => {
      setCatalogAttributes(prev => {
        const updatedByName = { ...prev.byName, [field]: value }
        const updatedNames = prev.names.includes(field) ? prev.names : [...prev.names, field]

        return {
          byName: updatedByName,
          names: updatedNames,
        }
      })

      removeFieldError(field)
    },
    [setCatalogAttributes, removeFieldError],
  )

  const providerValue = useMemo(
    () => ({
      uiState,
      selectedDynamicAttributes,
      fetchCategoryAttributes,
      getDynamicAttributeByCode,
      getAdditionalAttributesByCode,
      setSelectedDynamicAttributeValues,
      getSelectedDynamicAttributeValue,
      setSelectedDynamicAttributeValue,
    }),
    [
      uiState,
      selectedDynamicAttributes,
      fetchCategoryAttributes,
      getDynamicAttributeByCode,
      getAdditionalAttributesByCode,
      setSelectedDynamicAttributeValues,
      getSelectedDynamicAttributeValue,
      setSelectedDynamicAttributeValue,
    ],
  )

  return (
    <DynamicAttributesContext.Provider value={providerValue}>
      {children}
    </DynamicAttributesContext.Provider>
  )
}

export const useDynamicAttributesContext = () => {
  const context = useContext(DynamicAttributesContext)

  if (!context) {
    throw new Error(
      'useDynamicAttributesContext must be used within a DynamicAttributesContextProvider',
    )
  }

  return context
}
